import { computed, makeObservable } from 'mobx'
import type { Moment } from 'moment'
import moment from 'moment'
import type { $Values } from 'utility-types'

import { stringToDate } from 'modules/core/lib/dates'
import { Model } from 'modules/core/lib/mobx-rest'

import CompanyHoliday from 'modules/timeoff/store/models/CompanyHoliday'

export const ActionTypes = {
  CLOCK_OUT_ACTION: 'clock_out',
  FORGOT_CLOCK_OUT_ACTION: 'forgot_clock_out',
  CLOCK_IN_ACTION: 'clock_in',
  BREAK_START_ACTION: 'break_start',
  BREAK_END_ACTION: 'break_end',
}

export const ActionFrom = {
  TIME_TRACKING_WIDGET: 'time_tracking_widget',
  FORGOT_CLOCK_OUT_MODAL: 'forgot_clock_out_modal',
}
export const LOCATION_TYPES: { [key: string]: string } = {
  OFFICE: 'office',
  WORK_FROM_HOME: 'work_from_home',
  BUSINESS_TRIP: 'business_trip',
}

export type ActionType = $Values<typeof ActionTypes>

export const buildShift = (attr: Partial<IShift> = {}) => {
  return new Shift(buildShiftAttrs(attr))
}

const buildShiftAttrs = (attr: Partial<IShift> = {}) => {
  return {
    id: 0,
    period_id: 0,
    day: 0,
    clock_in: null,
    clock_out: null,
    minutes: 0,
    history: [],
    observations: null,
    date: '',
    reference_date: '',
    half_day: null,
    workable: false,
    automatic_clock_in: null,
    automatic_clock_out: null,
    clock_in_with_seconds: null,
    employee_id: 0,
    workplace_id: null,
    permissions: { history: false },
    location_type: null,
    time_settings_break_configuration_id: null,
    is_overnight: false,
    crosses_midnight: false,
    show_plus_one_day: false,
    in_source: null,
    out_source: null,
    in_location_latitude: null,
    in_location_longitude: null,
    in_location_accuracy: null,
    out_location_latitude: null,
    out_location_longitude: null,
    out_location_accuracy: null,
    created_at: '',
    company_id: 0,
    ...attr,
  }
}

export interface IShift {
  id: number
  period_id: number
  day: number
  clock_in: string | null
  clock_out: string | null
  minutes: number
  history: unknown[]
  observations: string | null
  date: string
  reference_date: string
  half_day: string | null
  workable: boolean
  automatic_clock_in: boolean | null
  automatic_clock_out: boolean | null
  clock_in_with_seconds: string | null
  employee_id: number
  workplace_id: number | null
  permissions: { history: boolean }
  location_type: string | null
  time_settings_break_configuration_id: number | null
  is_overnight: boolean
  crosses_midnight: boolean
  show_plus_one_day: boolean
  in_source: string | null
  out_source: string | null
  in_location_latitude: number | null
  in_location_longitude: number | null
  in_location_accuracy: number | null
  out_location_latitude: number | null
  out_location_longitude: number | null
  out_location_accuracy: number | null
  created_at: string
  company_id: number
}

class Shift extends Model<IShift> {
  constructor(attr: IShift) {
    super(attr)
    makeObservable(this, {
      date: computed,
      referenceDate: computed,
      clockIn: computed,
      isFirstHalf: computed,
      isSecondHalf: computed,
      isWorkable: computed,
      isHalfDay: computed,
      isAutomaticBreak: computed,
    })
  }

  static urlRoot() {
    return '/attendance/shifts'
  }

  get date(): moment.Moment {
    return stringToDate(this.attributes.date, 'backend')
  }

  get referenceDate(): moment.Moment {
    return stringToDate(this.attributes.reference_date, 'backend')
  }

  get clockIn(): moment.Moment {
    return moment(
      `${this.date.year()}/${this.date.month() + 1}/${this.attributes.day} ${
        this.attributes.clock_in_with_seconds
      }`,
      'YYYY/MM/D HH:mm:ss'
    )
  }

  get clockOut(): moment.Moment | null {
    if (!this.attributes.clock_out) return null

    return this.clockIn.clone().add(this.attributes.minutes ?? 0, 'minutes')
  }

  get isClosed(): boolean {
    return !this.isNew && !!this.attributes.clock_out
  }

  get isFirstHalf(): boolean {
    return this.attributes.half_day === CompanyHoliday.HalfDayTypes.beginning_of_day
  }

  get isSecondHalf(): boolean {
    return this.attributes.half_day === CompanyHoliday.HalfDayTypes.end_of_day
  }

  get isHalfDay(): boolean {
    return this.isFirstHalf || this.isSecondHalf
  }

  get isAutomaticBreak(): boolean {
    return !this.isWorkable && !!this.attributes.automatic_clock_in
  }

  get locationType(): string {
    return this.attributes.location_type ?? ''
  }

  get workplaceId(): string {
    const id = this.attributes.workplace_id
    if (typeof id === 'number') return id.toString()
    return id ?? ''
  }

  get isWorkable(): boolean {
    return this.attributes.workable
  }

  duration(): number {
    if (this.attributes.half_day) {
      return 0.5
    }

    if (this.attributes.clock_in && !this.attributes.clock_out) {
      return Math.floor(moment.duration(moment().diff(this.clockIn)).as('minutes'))
    }

    return this.attributes.minutes ?? 0
  }

  isOpen(date: Moment): boolean {
    return !!this.attributes.clock_in && !this.attributes.clock_out && date.isSame(moment(), 'day')
  }

  get isOpenShift(): boolean {
    return !!this.attributes.clock_in && !this.attributes.clock_out
  }

  get isEmpty(): boolean {
    return !this.attributes.clock_in && !this.attributes.clock_out
  }

  get isOvernight(): boolean {
    return this.attributes.is_overnight
  }

  get crossesMidnight(): boolean {
    return this.attributes.crosses_midnight
  }

  get showPlusOneDay(): boolean {
    return this.attributes.show_plus_one_day
  }

  get hasHistoryPermission(): boolean {
    return this.attributes.permissions.history
  }
}

export default Shift
